import React from 'react';
import { motion } from 'framer-motion';
import { Database, Brain, FileText, Leaf, Truck, BarChart3, Smartphone } from 'lucide-react';
import SectionWrapper from '../shared/SectionWrapper';

const features = [
  { icon: Database,   title: 'Data Collection',      description: 'Automated gathering from 100+ sources with smart validation.',        color: 'text-blue-500',   bg: 'bg-blue-50 dark:bg-blue-900/20' },
  { icon: Brain,      title: 'AI Analytics',          description: 'Deep learning identifies trends, risks, and opportunities.',           color: 'text-violet-500', bg: 'bg-violet-50 dark:bg-violet-900/20' },
  { icon: FileText,   title: 'ESG Reporting',         description: 'One-click reports for GRI, SASB, TCFD, CSRD & 30+ frameworks.',       color: 'text-emerald-500',bg: 'bg-emerald-50 dark:bg-emerald-900/20' },
  { icon: Leaf,       title: 'Carbon Accounting',     description: 'Scope 1, 2 & 3 emissions tracking with auto emission factors.',       color: 'text-green-500',  bg: 'bg-green-50 dark:bg-green-900/20' },
  { icon: Truck,      title: 'Supply Chain',          description: 'Monitor ESG risks across your entire supply chain network.',           color: 'text-orange-500', bg: 'bg-orange-50 dark:bg-orange-900/20' },
  { icon: BarChart3,  title: 'Real-time Dashboards',  description: 'Interactive drill-down dashboards with custom visualizations.',        color: 'text-indigo-500', bg: 'bg-indigo-50 dark:bg-indigo-900/20' },
  { icon: Smartphone, title: 'Mobile Access',         description: 'Full platform access on any device, anywhere, anytime.',              color: 'text-rose-500',   bg: 'bg-rose-50 dark:bg-rose-900/20' },
];

const FeaturesSection = () => (
  <SectionWrapper id="features" bgClass="bg-[#fafbfc] dark:bg-[#0a0f1a]" padding="py-10 md:py-12">

    {/* Header */}
    <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
      <h2
        className="font-extrabold tracking-tight text-gray-900 dark:text-white leading-tight"
        style={{ fontSize: 'clamp(1.75rem, 3.5vw, 2.75rem)' }}
      >
        Everything You Need for{' '}
        <span className="bg-gradient-to-r from-blue-600 to-cyan-500 bg-clip-text text-transparent">
          ESG Excellence
        </span>
      </h2>
      <p className="text-sm text-gray-500 dark:text-slate-400 sm:text-right sm:max-w-xs flex-shrink-0">
        One platform. Every tool you need to lead on sustainability.
      </p>
    </div>

    {/* Feature grid — 2 cols mobile, 4 cols desktop */}
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
      {features.map((f, i) => {
        const Icon = f.icon;
        return (
          <motion.div
            key={f.title}
            className="flex items-start gap-3 rounded-xl border border-gray-100 dark:border-slate-700/60 bg-white dark:bg-slate-800/60 px-4 py-4 hover:shadow-md hover:-translate-y-0.5 transition-all duration-200"
            initial={{ opacity: 0, y: 14 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-40px' }}
            transition={{ duration: 0.4, delay: i * 0.05, ease: [0.22, 1, 0.36, 1] }}
          >
            <div className={`flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center ${f.bg}`}>
              <Icon className={`w-4 h-4 ${f.color}`} strokeWidth={1.8} />
            </div>
            <div>
              <div className="text-sm font-bold text-gray-900 dark:text-white leading-snug">{f.title}</div>
              <div className="mt-0.5 text-xs text-gray-500 dark:text-slate-400 leading-relaxed">{f.description}</div>
            </div>
          </motion.div>
        );
      })}
    </div>

  </SectionWrapper>
);

export default FeaturesSection;
